import type { FeatureFlagConfig, ReleasePhaseInternal } from '@gonfalon/openapi/types';

export interface ProjectEnvironmentRef {
  key: string;
  name: string;
  critical: boolean;
  id: string;
}

export interface FlagEnvSummary {
  key: string;
  name: string;
  on: boolean;
  /** Target allocation percent 0–100 when known. */
  percent?: number;
  /** Compact method label, e.g. "Guarded rollout", "On", "Off". */
  method: string;
  /** Optional health/status label for an active automated release. */
  health?: string;
  /** Optional next-step hint when a later stage is scheduled. */
  nextStep?: string;
}

export function deriveFlagOverallLabel(environments: readonly FlagEnvSummary[]): string | undefined {
  if (environments.some((environment) => environment.health === 'Regressed')) {
    return 'Regressed';
  }
  if (environments.some((environment) => environment.method.toLowerCase().includes('rollout'))) {
    return 'Rolling out';
  }
  if (environments.length === 0) {
    return undefined;
  }
  if (environments.every((environment) => environment.on)) {
    return 'On';
  }
  if (environments.every((environment) => !environment.on)) {
    return 'Off';
  }
  return 'Mixed';
}

export type FlagOverallStatusTone = 'good' | 'bad' | 'caution' | 'neutral';

/** Shared badge tone for flag overall labels on cards and the detail panel. */
export function flagOverallStatusTone(label: string | undefined): FlagOverallStatusTone {
  if (label === 'On' || label === 'Rolling out') {
    return 'good';
  }
  if (label === 'Regressed') {
    return 'bad';
  }
  if (label === 'Off') {
    return 'caution';
  }
  return 'neutral';
}

const ACTIVE_RELEASE_STATUSES = new Set(['not_started', 'in_progress', 'monitoring_regressed']);

const TERMINAL_HEALTHY = new Set(['completed', 'manually_completed']);
const TERMINAL_REGRESSED = new Set(['reverted', 'manually_reverted', 'srm_stopped', 'monitoring_regressed']);

function isProductionLike(key: string, name: string): boolean {
  const haystack = `${key} ${name}`.toLowerCase();
  return haystack.includes('prod');
}

function isStagingOrDev(key: string, name: string): boolean {
  const haystack = `${key} ${name}`.toLowerCase();
  return haystack.includes('stag') || haystack.includes('dev') || haystack.includes('test');
}

function allocationPercent(allocation: number | undefined): number | undefined {
  if (typeof allocation !== 'number' || Number.isNaN(allocation)) {
    return undefined;
  }
  return Math.round(allocation / 1000);
}

function fallthroughPercent(config: FeatureFlagConfig | undefined): number | undefined {
  const variations = config?.fallthrough?.rollout?.variations;
  if (!variations?.length) {
    return undefined;
  }

  // Prefer the served/treatment share, not the majority control weight. For a
  // typical boolean rollout (25% true / 75% false) with offVariation=false,
  // Math.max would wrongly report 75%.
  const offVariation = config?.offVariation;
  const served =
    typeof offVariation === 'number'
      ? variations.filter((variation) => variation.variation !== offVariation && (variation.weight ?? 0) > 0)
      : variations.filter((variation) => (variation.weight ?? 0) > 0);

  if (served.length > 0) {
    return allocationPercent(Math.max(...served.map((variation) => variation.weight ?? 0)));
  }

  return allocationPercent(Math.max(...variations.map((variation) => variation.weight ?? 0)));
}

function releaseMethod(kind: ReleasePhaseInternal['kind'] | undefined): string {
  if (kind === 'guarded') {
    return 'Guarded rollout';
  }
  if (kind === 'progressive') {
    return 'Progressive rollout';
  }
  return 'Rollout';
}

function releaseHealth(status: ReleasePhaseInternal['status']): string | undefined {
  if (status === 'in_progress' || status === 'not_started') {
    return 'Healthy';
  }
  if (TERMINAL_HEALTHY.has(status)) {
    return 'Completed';
  }
  if (TERMINAL_REGRESSED.has(status)) {
    return status === 'monitoring_regressed' ? 'Regressed' : 'Stopped';
  }
  return undefined;
}

function formatStageDuration(durationMillis: number): string | undefined {
  if (durationMillis < 60_000) {
    return undefined;
  }
  if (durationMillis < 3_600_000) {
    const minutes = Math.max(1, Math.round(durationMillis / 60_000));
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  const hours = Math.max(1, Math.round(durationMillis / 3_600_000));
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

function nextStepLabel(release: ReleasePhaseInternal): string | undefined {
  // Regressed releases are paused — don't advertise a forward-looking next step.
  if (release.status !== 'not_started' && release.status !== 'in_progress') {
    return undefined;
  }
  const nextIndex = release.latestStageIndex + 1;
  const next = release.stages[nextIndex];
  if (!next) {
    return undefined;
  }
  const percent = allocationPercent(next.allocation);
  if (percent === undefined) {
    return undefined;
  }
  // Time until the next step is the current stage's dwell duration, not the next
  // stage's configured length.
  const current = release.stages[release.latestStageIndex];
  if (typeof current?.durationMillis === 'number' && current.durationMillis > 0) {
    const duration = formatStageDuration(current.durationMillis);
    if (duration) {
      return `Next ${percent}% in ${duration}`;
    }
  }
  return `Next ${percent}%`;
}

function pickReleaseForEnv(
  releases: readonly ReleasePhaseInternal[],
  environmentId: string,
): ReleasePhaseInternal | undefined {
  const forEnv = releases.filter((r) => r.environmentId === environmentId);
  if (forEnv.length === 0) {
    return undefined;
  }
  const active = forEnv.find((r) => ACTIVE_RELEASE_STATUSES.has(r.status));
  return active ?? forEnv[0];
}

function summarizeEnv(
  env: ProjectEnvironmentRef,
  config: FeatureFlagConfig | undefined,
  release: ReleasePhaseInternal | undefined,
): FlagEnvSummary {
  const on = Boolean(config?.on);
  if (release && ACTIVE_RELEASE_STATUSES.has(release.status)) {
    const stage = release.stages[release.latestStageIndex];
    return {
      key: env.key,
      name: env.name || env.key,
      on,
      percent: allocationPercent(stage?.allocation) ?? fallthroughPercent(config),
      method: releaseMethod(release.kind),
      health: releaseHealth(release.status),
      nextStep: nextStepLabel(release),
    };
  }

  if (!on) {
    return { key: env.key, name: env.name || env.key, on: false, percent: 0, method: 'Off' };
  }

  const percent = fallthroughPercent(config);
  if (percent !== undefined && percent < 100) {
    return {
      key: env.key,
      name: env.name || env.key,
      on: true,
      percent,
      method: 'Percent rollout',
    };
  }

  return {
    key: env.key,
    name: env.name || env.key,
    on: true,
    percent: 100,
    method: 'On',
  };
}

/**
 * Rank and trim critical flag environments for artifact rollout status: active-
 * rollout envs first, then production-like, then staging/dev, then remaining.
 * Non-critical environments are excluded. Caps at `max`.
 */
export function deriveFlagEnvSummaries({
  flagEnvironments,
  projectEnvironments,
  releasePhases = [],
  max = 3,
}: {
  flagEnvironments?: Record<string, FeatureFlagConfig> | null;
  projectEnvironments: readonly ProjectEnvironmentRef[];
  releasePhases?: readonly ReleasePhaseInternal[];
  max?: number;
}): FlagEnvSummary[] {
  if (projectEnvironments.length === 0 || max <= 0) {
    return [];
  }

  const criticalEnvironments = projectEnvironments.filter((env) => env.critical);
  if (criticalEnvironments.length === 0) {
    return [];
  }

  const configs = flagEnvironments ?? {};
  const releaseByEnvId = new Map<string, ReleasePhaseInternal>();
  for (const env of criticalEnvironments) {
    const release = pickReleaseForEnv(releasePhases, env.id);
    if (release) {
      releaseByEnvId.set(env.id, release);
    }
  }

  const scored = criticalEnvironments.map((env, index) => {
    const release = releaseByEnvId.get(env.id);
    const hasActiveRollout = Boolean(release && ACTIVE_RELEASE_STATUSES.has(release.status));
    // Prefer staging over development within the staging/dev tier.
    const stagingBoost = env.key.toLowerCase().includes('stag') || env.name.toLowerCase().includes('stag') ? 0 : 1;
    let tier = 3;
    if (hasActiveRollout) {
      tier = 0;
    } else if (isProductionLike(env.key, env.name)) {
      tier = 1;
    } else if (isStagingOrDev(env.key, env.name)) {
      tier = 2;
    }
    return { env, tier, stagingBoost, index, release };
  });

  scored.sort((a, b) => a.tier - b.tier || a.stagingBoost - b.stagingBoost || a.index - b.index);

  return scored.slice(0, max).map(({ env, release }) => summarizeEnv(env, configs[env.key], release));
}
